import React from 'react';
import ReactDOM from 'react-dom/client';
import { CreatorApp } from './CreatorApp';
import type { CreatorAppProps } from './types';

/**
 * Render function for UMD build usage
 * Enables embedding via script tag
 * 
 * @example
 * ```html
 * <script src="simple-creator.umd.js"></script>
 * <div id="creator-container"></div>
 * <script>
 *   SimpleCreator.render('creator-container', {
 *     mode: 'create',
 *     onSuccess: (event) => {
 *       console.log('Event created:', event);
 *     }
 *   });
 * </script>
 * ```
 */
export function render(
  containerId: string | HTMLElement,
  props?: CreatorAppProps
): {
  unmount: () => void;
  update: (newProps: CreatorAppProps) => void;
} {
  // Get container element
  const container = typeof containerId === 'string' 
    ? document.getElementById(containerId)
    : containerId;

  if (!container) {
    throw new Error(`Container element not found: ${containerId}`);
  }

  // Create React root
  const root = ReactDOM.createRoot(container);

  // Render component
  const renderComponent = (componentProps: CreatorAppProps = {}) => {
    root.render(
      React.createElement(CreatorApp, componentProps)
    );
  };

  // Initial render
  renderComponent(props);

  // Return control methods
  return {
    unmount: () => root.unmount(),
    update: (newProps: CreatorAppProps) => renderComponent(newProps)
  };
}

// Attach to window for UMD build
if (typeof window !== 'undefined') {
  (window as any).SimpleCreator = {
    render,
    CreatorApp,
    version: '0.1.0'
  };
}
